import { Inject, Injectable, Renderer2, RendererFactory2 } from "@angular/core";
import { Store } from "@ngrx/store";

import { Observable, merge, of, timer } from "rxjs";
import { filter, map, mapTo, take } from "rxjs/operators";
import { selectKeycloakInitialized } from "src/app/core/ngrx/ui";

import { WINDOW } from "src/app/core/services/window.service";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: "root",
})
export class SnippetService {
  segmentScriptAdded = false;
  googleTagManagerScriptAdded = false;
  friendbuyScriptAdded = false;
  kcInitTimeout = 5000; // ms
  surveyRetries = 10;
  surveyDelay = 250; // ms
  surveyAnalyticsError = new Error("Analytics not initialized");
  surveyFlagName = "enable-surveys";

  private _renderer: Renderer2;

  private get renderer(): Renderer2 {
    if (!this._renderer) {
      this._renderer = this.rendererFactory.createRenderer(null, null);
    }
    return this._renderer;
  }

  constructor(
    private store$: Store,
    private rendererFactory: RendererFactory2,
    @Inject(WINDOW) private window: Window
  ) {}

  generateSnippets(): Observable<boolean> {
    const loadSegment =
      environment.segmentConfig.track && !this.segmentScriptAdded;
    const kcInitializationObservableWithTimeout = merge(
      this.store$.select(selectKeycloakInitialized),
      timer(this.kcInitTimeout).pipe(mapTo(false))
    );

    if (!loadSegment) {
      return of(false);
    }

    return kcInitializationObservableWithTimeout.pipe(
      filter((init) => init !== null),
      take(1), // Observable must complete
      map((initialized) => {
        if (!initialized) {
          return true;
        }
        if (loadSegment) {
          this.addSegmentSnippet();
        }
        if (!this.googleTagManagerScriptAdded) {
          this.handleGoogleTagManager();
        }
        if (!this.friendbuyScriptAdded) {
          this.addFriendbuySnippet();
        }
        this.addFeatherySnippet();
        return true;
      })
    );
  }

  private addSegmentSnippet() {
    const analyticsScript = this.renderer.createElement("script");
    analyticsScript.type = "text/javascript";
    analyticsScript.async = true;
    analyticsScript.text = environment.segmentConfig.snippet;

    this.renderer.appendChild(this.window.document.head, analyticsScript);

    this.segmentScriptAdded = true;
  }

  private addFeatherySnippet() {
    const featheryScript = this.renderer.createElement("script");
    featheryScript.type = "text/javascript";
    featheryScript.async = true;
    featheryScript.src =
      "https://cdn.jsdelivr.net/npm/@feathery/react@latest/umd/index.js";

    this.renderer.appendChild(this.window.document.head, featheryScript);
  }

  private handleGoogleTagManager() {
    var gtmScript = this.renderer.createElement("script");
    gtmScript.type = "text/javascript";
    gtmScript.async = true;
    gtmScript.text = environment.googleTagManagerConfig.snippetHeader;
    this.renderer.appendChild(this.window.document.head, gtmScript);

    this.googleTagManagerScriptAdded = true;
  }

  private addFriendbuySnippet() {
    const friendbuyScript = this.renderer.createElement("script");
    friendbuyScript.type = "text/javascript";
    friendbuyScript.async = true;
    friendbuyScript.text = environment.friendbuyConfig.snippet;

    this.renderer.appendChild(this.window.document.head, friendbuyScript);

    this.friendbuyScriptAdded = true;
  }
}
